import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Star, Clock, Sparkles, Heart, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`);
        const [servicesRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/testimonials`)
        ]);
        setServices(servicesRes.data.slice(0, 6));
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-[#FFFCF9] overflow-hidden">
        {/* Background Image - Right Side */}
        <div className="absolute right-0 top-0 bottom-0 w-full lg:w-1/2 opacity-10 lg:opacity-100">
          <img
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_1c26490f-4cbf-4297-b6f0-da0519adec07/artifacts/15dbubh1_3b425c84d62b48f9f14152b049ddec68e755f4f3f4f384593d93bd69f71c346a.jpeg"
            alt="Kriel Colon and Beauty Spa treatment room - hot stone massage"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFCF9] via-[#FFFCF9]/80 to-transparent lg:from-[#FFFCF9] lg:via-transparent lg:to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="max-w-xl fade-in">
            <span className="inline-block text-spa-pink-500 font-medium text-sm tracking-widest uppercase mb-6">
              Welcome to Our Spa
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-spa-neutral-800 mb-6 leading-tight">
              Relax, Refresh,<br />
              <span className="italic text-spa-pink-500">Rejuvenate</span>
            </h1>
            <p className="text-lg text-spa-neutral-500 leading-relaxed mb-10 max-w-md">
              Where beauty meets art. Treat yourself to a luxurious experience with our expert health and beauty services at Kriel Colon and Beauty Spa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/booking" data-testid="hero-book-now">
                <Button className="bg-spa-pink hover:bg-spa-pink-600 text-white rounded-lg px-8 py-6 text-base font-medium transition-all hover:shadow-lg">
                  Book Appointment
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/services" data-testid="hero-view-services">
                <Button variant="outline" className="rounded-lg px-8 py-6 text-base font-medium border-2 border-spa-pink-300 text-spa-pink-600 hover:bg-spa-pink-50 transition-all">
                  View Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Sparkles, title: "Professional Care", desc: "Expert therapists with years of experience" },
              { icon: Heart, title: "Relaxing Atmosphere", desc: "Serene environment for complete relaxation" },
              { icon: Leaf, title: "Natural Products", desc: "Premium quality products for your skin" },
            ].map((feature, idx) => (
              <div key={idx} className="text-center p-8 rounded-2xl bg-spa-neutral-50 spa-hover">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-spa-pink-100 rounded-full mb-6">
                  <feature.icon className="h-8 w-8 text-spa-pink-500" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-spa-neutral-800 mb-3">{feature.title}</h3>
                <p className="text-spa-neutral-500">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-[#FFFCF9]" data-testid="featured-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-spa-pink-500 font-medium text-sm tracking-widest uppercase">
              Our Specialties
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-spa-neutral-800 mt-4">
              Popular Treatments
            </h2>
            <p className="text-spa-neutral-500 text-lg mt-4 max-w-2xl mx-auto">
              Discover our most sought-after services designed to help you look and feel your best
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse bg-spa-neutral-100 rounded-2xl h-96" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, idx) => (
                <Card key={service.id} className="service-card group overflow-hidden rounded-2xl border-0 bg-white spa-shadow" data-testid={`service-card-${idx}`}>
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={service.image_url || "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400"}
                      alt={service.name}
                      className="service-image w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-spa-neutral-900/50 to-transparent" />
                    <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-spa-pink-500 text-xs font-medium px-3 py-1 rounded-full">
                      {service.category}
                    </span>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-heading text-xl font-semibold text-spa-neutral-800 mb-2">
                      {service.name}
                    </h3>
                    <p className="text-spa-neutral-500 text-sm mb-4 line-clamp-2">
                      {service.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-spa-neutral-400">
                        <Clock className="h-4 w-4" />
                        <span className="text-sm">{service.duration} min</span>
                      </div>
                      <Link to="/booking">
                        <Button size="sm" className="bg-spa-pink hover:bg-spa-pink-600 text-white rounded-lg text-sm">
                          Book Now
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" className="rounded-lg px-8 py-6 text-base font-medium border-2 border-spa-pink-300 text-spa-pink-600 hover:bg-spa-pink-50 transition-all">
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Spa Gallery */}
      <section className="py-24 bg-white" data-testid="spa-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-spa-pink-500 font-medium text-sm tracking-widest uppercase">
              Our Space
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-spa-neutral-800 mt-4">
              Inside Our Spa
            </h2>
            <p className="text-spa-neutral-500 text-lg mt-4 max-w-2xl mx-auto">
              Take a glimpse into our welcoming and professional spa environment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_1c26490f-4cbf-4297-b6f0-da0519adec07/artifacts/vswpxymz_%20Pedicure%20Room.jpeg"
                alt="Pedicure Room"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-spa-neutral-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white font-medium">Pedicure Room</span>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_1c26490f-4cbf-4297-b6f0-da0519adec07/artifacts/15dbubh1_3b425c84d62b48f9f14152b049ddec68e755f4f3f4f384593d93bd69f71c346a.jpeg"
                alt="Hot Stone Massage"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-spa-neutral-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white font-medium">Treatment Room</span>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_1c26490f-4cbf-4297-b6f0-da0519adec07/artifacts/bwrvz5oz_colonnel.jpeg"
                alt="Our Staff"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-spa-neutral-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white font-medium">Our Team</span>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_1c26490f-4cbf-4297-b6f0-da0519adec07/artifacts/o2banri2_Treatment%20In%20Progress%20Sign.jpeg"
                alt="Treatment In Progress"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-spa-neutral-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <span className="text-white font-medium">Professional Service</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-spa-pink-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-spa-pink-500 font-medium text-sm tracking-widest uppercase">
              Client Stories
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-spa-neutral-800 mt-4">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <Card key={testimonial.id} className="testimonial-card relative bg-white rounded-2xl border-0 spa-shadow p-8" data-testid={`testimonial-${idx}`}>
                <CardContent className="p-0">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-spa-pink text-spa-pink" />
                    ))}
                  </div>
                  <p className="text-spa-neutral-600 leading-relaxed mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <p className="font-heading font-semibold text-spa-neutral-800">
                    {testimonial.client_name}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-spa-pink relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-heading text-4xl md:text-5xl text-white mb-6">
            Ready to Experience Relaxation?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Book your appointment today and let our expert therapists help you look and feel your best.
          </p>
          <Link to="/booking" data-testid="cta-book-now">
            <Button className="bg-white text-spa-pink hover:bg-spa-neutral-100 rounded-lg px-10 py-6 text-lg font-semibold transition-all hover:shadow-lg">
              Book Your Session
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Business Hours */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-spa-pink-500 font-medium text-sm tracking-widest uppercase">
                Visit Us
              </span>
              <h2 className="font-heading text-4xl text-spa-neutral-800 mt-4 mb-6">
                Business Hours
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-spa-neutral-200">
                  <span className="text-spa-neutral-600">Monday - Friday</span>
                  <span className="font-medium text-spa-neutral-800">08:00 - 17:00</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-spa-neutral-200">
                  <span className="text-spa-neutral-600">Saturday</span>
                  <span className="font-medium text-spa-neutral-800">08:00 - 15:00</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-spa-neutral-200">
                  <span className="text-spa-neutral-600">Sunday</span>
                  <span className="font-medium text-spa-pink">Closed</span>
                </div>
              </div>
            </div>
            <div className="bg-spa-neutral-50 rounded-2xl p-8">
              <h3 className="font-heading text-2xl font-semibold text-spa-neutral-800 mb-4">
                Location
              </h3>
              <p className="text-spa-neutral-600 mb-4">
                32 Kraanvoel Avenue<br />
                Kriel
              </p>
              <p className="text-spa-neutral-600 mb-6">
                <strong>Phone:</strong> 065 902 9122<br />
                <strong>Email:</strong> krielcolonandbeautyspa@gmail.com
              </p>
              <Link to="/contact">
                <Button className="bg-spa-pink hover:bg-spa-pink-600 text-white rounded-lg">
                  Get Directions
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
